import { extractNumericEntityIdFromDataId, extractEntityIdFromDataId } from './treeUtils';
import { TreeNodeFactory } from './treeNodeFactory';
import { TreeNode } from '@/types/tree';
import { IBackend } from '@/api/BackendApi';
import { ApiException } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { invalidateSampleGridCache } from '@/lib/cache/sampleGridCache';
import { getProblemDetail } from '@/lib/utils/apiErrors';

/**
 * Interface for reset operation dependencies
 */
export interface IResetDependencies {
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
    autoHideDuration?: number | null,
    title?: string
  ) => void;
  showConfirmDialog: (config: {
    title: string;
    message: string;
    confirmText?: string;
    isDestructive?: boolean;
  }) => Promise<boolean>;
  getData?: (dataId: string) => any;
}

/**
 * Performs the actual reset operation based on node type
 * This is the core reset logic shared between useResetEntity and actionHandlers
 */
export async function performResetOperation(node: TreeNode, backend: IBackend): Promise<any> {
  let updatedData;
  switch (node.type) {
    case 'sample': {
      updatedData = await backend.sample.resetPending(extractEntityIdFromDataId(node.dataId).toString());
      invalidateSampleGridCache();
      break;
    }
    case 'sampleActivity': {
      updatedData = await backend.sampleActivity.resetPending(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'projectActivity': {
      updatedData = await backend.projectActivity.resetPending(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'theoryActivity': {
      updatedData = await backend.theoryActivity.resetPending(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'dataPackage': {
      updatedData = await backend.data.resetPending(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'shipping': {
      updatedData = await backend.shipping.resetPending(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    default:
      throw new Error(`Unsupported node type for reset: ${node.type}`);
  }

  log.debug('[resetEntityUtils] Node reset successfully:', {
    nodeId: node.nodeId,
    type: node.type,
  });

  return updatedData;
}

/**
 * Handles reset errors with proper error codes (428, 424)
 * Returns true if reset should be retried with force, false if it should be aborted
 */
export async function handleResetError(
  error: unknown,
  node: TreeNode,
  deps: IResetDependencies
): Promise<{ shouldRetry: boolean; shouldAbort: boolean }> {
  if (!ApiException.isApiException(error)) {
    deps.showSnackbar('Unexpected error occurred while resetting.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }

  if (error.status === 428) {
    // Requires confirmation - show additional dialog
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    const forceConfirmed = await deps.showConfirmDialog({
      title: `Force Reset ${entityTypeLabel}`,
      message: getProblemDetail(error) || `This ${entityTypeLabel} has issues. Are you sure you want to reset anyway?`,
      confirmText: 'Reset Anyway',
      isDestructive: false,
    });

    if (forceConfirmed) {
      return { shouldRetry: true, shouldAbort: false };
    }
    return { shouldRetry: false, shouldAbort: true };
  } else if (error.status === 424) {
    // Cannot reset - show detailed error message
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    deps.showSnackbar(
      getProblemDetail(error) || `${entityTypeLabel} ${node.label} cannot be reset due to validation issues.`,
      'error',
      null, // do not hide automatically, leave to user to close manually
      `${entityTypeLabel} ${node.label} cannot be reset. Please correct the following issues:`
    );
    return { shouldRetry: false, shouldAbort: true };
  } else {
    deps.showSnackbar(getProblemDetail(error) ?? 'Failed to reset.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }
}
